import React from 'react'
import clsx from 'clsx'
import { useIntl, FormattedMessage } from 'react-intl'
import { Button, Theme, ButtonGroup } from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import CaretLeftIcon from '@icons/CaretLeftIcon'
import CaretRightIcon from '@icons/CaretRightIcon'
import messages from '@src/utils/messages'

const useStyles = makeStyles((theme: Theme) => ({
  paginationContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    [theme.breakpoints.up('md')]: {
      justifyContent: 'flex-end',
    },
  },
  pageLabel: {
    display: 'none',
    marginRight: theme.spacing(3),
    [theme.breakpoints.up('md')]: {
      display: 'block',
      minWidth: 96,
      textAlign: 'right',
    },
  },
  buttonGroup: {
    background: theme.palette.common.white,
  },
  buttonRoot: {
    padding: 10,
    height: 44,
    width: 44,
    minWidth: 31,
    [theme.breakpoints.up('md')]: {
      height: 32,
      width: 32,
    },
    // custom focus state
    '&:focus-visible': {
      boxShadow: `inset 0 0 0 1px ${theme.palette.primary.main}, 0 0 6px 0 rgba(15, 105, 175, 0.5)`,
    },
  },
  buttonOutlined: {
    borderColor: theme.palette.grey[500],
    '&$buttonDisabled': {
      borderColor: theme.palette.grey[500],
    },
  },
  buttonDisabled: {},
  buttonIcon: {
    fontSize: theme.typography.pxToRem(16),
    color: theme.palette.primary.main,
    [theme.breakpoints.up('md')]: {
      fontSize: theme.typography.pxToRem(12),
    },
  },
  buttonIconDisabled: {
    color: theme.palette.grey[500],
  },
  mobilePageCountLabel: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: `1px solid ${theme.palette.grey[500]}`,
    padding: theme.spacing(0, 4),
    fontSize: theme.typography.pxToRem(14),
    fontWeight: theme.typography.fontWeightRegular,
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
    fontFamily: 'Lato',
  },
}))

interface Props {
  loading?: boolean
  numPages: number
  currentPage: number
  handleUpdatePage: (page: number) => void
  customCounterStyling?: string
  customButtonStyling?: string
}

const ResponsivePagination: React.FC<Props> = ({
  loading,
  handleUpdatePage,
  currentPage,
  numPages = 0,
  customCounterStyling,
  customButtonStyling,
}) => {
  const classes = useStyles()
  const intl = useIntl()

  const changePage = (selectedPage: number) => {
    handleUpdatePage(selectedPage)
  }

  const firstPage = currentPage === 1 || currentPage === 0
  const lastPage = currentPage === numPages
  const pageLabel = (
    <FormattedMessage
      {...messages.PAGINATION_OPTION_DISPLAY}
      values={{ page: currentPage, numberOfPages: numPages }}
    />
  )

  return (
    <div className={classes.paginationContainer}>
      <div className={classes.pageLabel}>{pageLabel}</div>

      <ButtonGroup
        className={classes.buttonGroup}
        disableElevation
        disableFocusRipple
        disableRipple
        aria-label={intl.formatMessage(messages.PAGINATION_LABEL_NAVIGATION)}
      >
        <Button
          classes={{
            root: clsx(classes.buttonRoot, customButtonStyling),
            outlined: classes.buttonOutlined,
            disabled: classes.buttonDisabled,
          }}
          aria-label={intl.formatMessage(messages.PAGINATION_LABEL_PREVIOUS)}
          disabled={firstPage || loading}
          onClick={() => changePage(currentPage - 1)}
        >
          <CaretLeftIcon
            className={clsx(classes.buttonIcon, {
              [classes.buttonIconDisabled]: firstPage,
            })}
          />
        </Button>
        <Button
          className={clsx(classes.mobilePageCountLabel, customCounterStyling)}
        >
          {pageLabel}
        </Button>
        <Button
          classes={{
            root: clsx(classes.buttonRoot, customButtonStyling),
            outlined: classes.buttonOutlined,
            disabled: classes.buttonDisabled,
          }}
          aria-label={intl.formatMessage(messages.PAGINATION_LABEL_NEXT)}
          disabled={lastPage || loading}
          onClick={() => changePage(currentPage + 1)}
        >
          <CaretRightIcon
            className={clsx(classes.buttonIcon, {
              [classes.buttonIconDisabled]: lastPage,
            })}
          />
        </Button>
      </ButtonGroup>
    </div>
  )
}

export default ResponsivePagination
